import { useCallback, useEffect, useRef, useState } from 'react';
import { getSandbox, getSandboxState, resetSleptByChoice, sandboxSleptByChoice } from '../services/SandboxService';
import { noteStateWhileSleeping, sleepIsSettling } from '../utils/sandboxSleep';

/**
 * The person's sandbox overview (`GET /api/v1/sandbox`), refreshed every `intervalMs` while the page is visible.
 * Returns `{ sandbox, error, loading, refresh }`; `sandbox` keeps its last good value while a refresh fails.
 * After the person put it to sleep (sleepSandbox), the overview is not asked (asking wakes the pod): the state is
 * (`GET /api/v1/sandbox/state`, never waking), and the sandbox shows asleep until it runs again.
 */
export default function useSandbox({ intervalMs = 10000, enabled = true } = {}) {
  const [sandbox, setSandbox] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const active = useRef(true);

  const refresh = useCallback(async() => {
    try {
      if (sandboxSleptByChoice()) {
        const now = await getSandboxState().catch(() => null);
        if (now) { noteStateWhileSleeping(now.state); }
        if (now && (now.state !== 'running' || sleepIsSettling())) {
          if (active.current) {
            setSandbox((previous) => ({ ...(previous || {}), state: 'asleep', services: [] }));
            setError(null);
          }
          return;
        }
        // Woken elsewhere (a project, an AI tool): the overview again.
        resetSleptByChoice();
      }
      const next = await getSandbox();
      if (active.current) {
        setSandbox(next);
        setError(null);
      }
    } catch (err) {
      if (active.current) { setError(err); }
    } finally {
      if (active.current) { setLoading(false); }
    }
  }, []);

  useEffect(() => {
    active.current = true;
    if (!enabled) { return () => { active.current = false; }; }
    refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') { refresh(); }
    }, intervalMs);
    return () => {
      active.current = false;
      clearInterval(timer);
    };
  }, [enabled, intervalMs, refresh]);

  return { sandbox, error, loading, refresh };
}
